import type { FileUploadRestrictions } from '../types';
import type { UppyLocale } from './getUppyLocale';
import type { Meta } from '@uppy/core';

import Uppy from '@uppy/core';
import { useEffect, useMemo, useState } from 'react';

import { getUppyLocale } from './getUppyLocale';
import { parseFileUploadRestrictions } from './parseFileUploadRestrictions';

type UppyInstance = Uppy<Meta, Record<string, never>>;

type UppyInstanceOptions = {
  autoProceed?: boolean;
  /** Registers plugins on the new instance; read once, on creation. */
  configure?: (uppy: UppyInstance) => UppyInstance;
  id: string;
  language: string;
  meta?: Meta;
  restrictions?: FileUploadRestrictions;
  uppyLocaleOverrides?: Partial<UppyLocale['strings']>;
};

/**
 * Creates the Uppy instance behind every uploader surface, once per mount,
 * and keeps its locale and restrictions in sync with later renders.
 *
 * @param id - Unique per mounted instance; Uppy derives its file input ids
 *   and GoldenRetriever its storage key from it.
 * @param language - App language that picks the Uppy locale pack; unknown
 *   languages fall back to English.
 * @returns `uppy` and `validatedRestrictions`: the restrictions Uppy actually
 *   enforces after `parseFileUploadRestrictions`, which falls back to its
 *   defaults when the input fails validation. Describe restrictions to the
 *   user from these, not from the raw prop.
 */
export function useUppyInstance({
  autoProceed = false,
  configure,
  id,
  language,
  meta,
  restrictions,
  uppyLocaleOverrides,
}: UppyInstanceOptions) {
  const validatedRestrictions = useMemo(
    () =>
      restrictions ? parseFileUploadRestrictions(restrictions) : undefined,
    [restrictions],
  );
  const uppyLocale = useMemo(
    () => getUppyLocale(language, uppyLocaleOverrides),
    [language, uppyLocaleOverrides],
  );

  const [uppy] = useState(() => {
    const instance: UppyInstance = new Uppy({
      autoProceed,
      id,
      locale: uppyLocale,
      meta,
      restrictions: validatedRestrictions,
    });

    return configure ? configure(instance) : instance;
  });

  useEffect(() => {
    uppy.setOptions({ locale: uppyLocale });
  }, [uppy, uppyLocale]);

  useEffect(() => {
    uppy.setOptions({
      restrictions: validatedRestrictions ?? {
        allowedFileTypes: null,
        maxFileSize: null,
        maxNumberOfFiles: null,
        maxTotalFileSize: null,
        minFileSize: null,
        minNumberOfFiles: null,
        requiredMetaFields: [],
      },
    });
  }, [uppy, validatedRestrictions]);

  return {
    uppy,
    validatedRestrictions,
  };
}
